
import React, { useState, useMemo, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Tracks from './pages/Tracks';
import Register from './pages/Register';
import CeremonyRegister from './pages/CeremonyRegister';
import VolunteerRegister from './pages/VolunteerRegister';
// Admin imports
import { AdminProvider } from './admin/AdminContext';
import { Login, Dashboard, Users, Registrations, Export, ChangePassword } from './admin/pages';
import Volunteers from './admin/pages/Volunteers';
import CeremonyRegistrations from './admin/pages/CeremonyRegistrations';
import ProtectedRoute from './admin/ProtectedRoute';
import AdminNav from './admin/AdminNav';

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

function App() {
  const [mode, setMode] = useState('light');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = useMemo(
    () => createTheme({
  mode,
  palette: {
    mode,
    primary: {
      main: '#006400', // Lagos green - representing growth and nature
    },
    secondary: {
      main: '#FFD700', // Lagos gold - representing prosperity and wealth
    },
    accent: {
      main: '#FF6B35', // Vibrant orange-red for energy
      light: '#FF8C42',
      dark: '#E55A2B',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    ...(mode === 'dark' && {
      background: {
        default: '#121212',
        paper: '#1E1E1E',
      },
    }),
  },
  typography: {
    fontFamily: '"Oxygen", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      // Use default text color for better contrast in both light and dark modes
    },
    h2: {
      fontWeight: 600,
      // Inherit text color from theme to ensure readability in dark mode
    },
    h3: {
      fontWeight: 600,
      // Avoid hard-coded colors; rely on theme.palette.text.primary
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #006400 30%, #2E7D32 90%)',
          boxShadow: '0 3px 5px 2px rgba(0, 100, 0, .3)',
          '&:hover': {
            background: 'linear-gradient(45deg, #2E7D32 30%, #006400 90%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(45deg, #FFD700 30%, #FFA000 90%)',
          color: '#000',
          boxShadow: '0 3px 5px 2px rgba(255, 215, 0, .3)',
          '&:hover': {
            background: 'linear-gradient(45deg, #FFA000 30%, #FFD700 90%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #E0E0E0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
        },
        colorSecondary: {
          backgroundColor: '#FFF8E1',
          color: '#F57C00',
        },
      },
    },
  },
}), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AdminProvider>
            <Navbar />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/tracks" element={<Tracks />} />
              <Route path="/register" element={<Register />} />
              <Route path="/ceremony" element={<CeremonyRegister />} />
              <Route path="/volunteer" element={<VolunteerRegister />} />
              {/* Admin login route (public) */}
              <Route path="/admin/login" element={<Login />} />
              {/* Admin protected routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminNav /><Dashboard /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute><AdminNav /><Users /></ProtectedRoute>} />
              <Route path="/admin/registrations" element={<ProtectedRoute><AdminNav /><Registrations /></ProtectedRoute>} />
              <Route path="/admin/volunteers" element={<ProtectedRoute><AdminNav /><Volunteers /></ProtectedRoute>} />
              <Route path="/admin/ceremony" element={<ProtectedRoute><AdminNav /><CeremonyRegistrations /></ProtectedRoute>} />
              <Route path="/admin/export" element={<ProtectedRoute><AdminNav /><Export /></ProtectedRoute>} />
              <Route path="/admin/change-password" element={<ProtectedRoute><AdminNav /><ChangePassword /></ProtectedRoute>} />
            </Routes>
            <Footer />
          </AdminProvider>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
